/**
 * RESPONSIBILITY: every event on the site, the tags they may carry, and the words above
 * every band that shows them.
 *
 * ONE LIST. A special event is not a different kind of thing kept somewhere else — it is
 * an event with `special` ticked, exactly as a featured one is an event with `featured`
 * ticked. Two lists would mean deciding which one a thing belongs in before you could
 * enter it, and moving it later would mean retyping it.
 *
 * The headings live here with the events on purpose. They were on a separate page
 * section, which meant the band you were looking at and the pencil that edited it were
 * different things in different places - open the pencil over "All Events" and the list
 * was not in it.
 *
 * Capped at 80: a section is stored as one document and every saved revision keeps a
 * whole copy of it, which is right for dozens of rows and wrong for thousands.
 *
 * No @EditableBy, so WRITE defaults to NONE - admin only.
 */
import { Section, Text, Paragraph, List, StringList } from '@framework/core/decorators';
import { SectionName } from '../section-names';
import { EventRow } from './rows/event';
import { withUrls } from './event-url';

@Section({ name: SectionName.Events, label: 'Events' })
export class EventsSection {
  @Text({
    label: 'Featured band title', maxLength: 120, default: 'Featured Events',
    group: 'Band headings', collapsed: true,
    description: 'Over the events with Featured ticked.',
  })
  featuredHeading!: string;

  @Text({
    label: 'All events band title', maxLength: 120, default: 'All Events',
    group: 'Band headings', description: 'Over the full list.',
  })
  allHeading!: string;

  @Text({
    label: 'Special band title', maxLength: 120, default: 'Special Events',
    group: 'Band headings', description: 'Over the events with Special ticked.',
  })
  specialHeading!: string;

  @Paragraph({
    label: 'Special band description', maxLength: 4000, rows: 2, group: 'Band headings',
  })
  specialIntro!: string;

  /**
   * What an event's Tags picker offers. Grow it and every picker follows; the write path
   * refuses a tag that is not on this list.
   */
  @StringList({
    label: 'Tags', maxItems: 24, group: 'Band headings',
    description: 'What an event may be tagged with. Bands elsewhere pick events by tag.',
    default: ['Worship', 'Children', 'Prayer', 'Women', 'Men', 'Young Adults', 'Mission', 'Bible Study', 'Seasonal'],
  })
  categories!: string[];

  @Text({
    label: 'Default button text', maxLength: 120, default: 'Details',
    group: 'Filters', collapsed: true,
    description: 'The word on a row’s button when the event gives it none of its own.',
  })
  detailsLabel!: string;

  @Text({
    label: 'Search box prompt', maxLength: 300, default: 'Search by event name',
    group: 'Filters',
  })
  searchPlaceholder!: string;

  @Text({
    label: 'Date picker label', maxLength: 120, default: 'Choose dates', group: 'Filters',
  })
  datesLabel!: string;

  @Text({
    label: 'Hint under the tags', maxLength: 600, group: 'Filters',
    default: 'Ctrl-click (⌘-click on a Mac) to combine tags.',
    description: 'Only shown once there is more than one tag to combine.',
  })
  combineHint!: string;

  @Text({
    label: 'Title', maxLength: 300, default: 'That event has moved on',
    group: 'When the link is old', collapsed: true,
    description: 'Shown when a link points at an event that is no longer in the list.',
  })
  missingHeading!: string;

  @Paragraph({
    label: 'Text', maxLength: 4000, rows: 2, group: 'When the link is old',
    default: 'It may have been removed, or the link may be from an older version of the page.',
  })
  missingBody!: string;

  @Text({
    label: 'Button text', maxLength: 120, default: 'See all events',
    group: 'When the link is old',
  })
  missingCtaLabel!: string;

  @List({ label: 'Events', of: () => EventRow, maxItems: 80 })
  events!: EventRow[];

  /**
   * Fills in each event's `url`. Here and not on the row, because uniqueness is a fact
   * about the LIST — a row cannot know what the row above it has already claimed.
   */
  static prepare(next: Record<string, unknown>, previous: Record<string, unknown> | undefined) {
    const rows = (next.events ?? []) as EventRow[];
    const before = previous?.events as EventRow[] | undefined;
    return { ...next, events: withUrls(rows, before) };
  }

  /* ---- the panel beside an event, on its own page ---- */

  @Text({
    label: 'Panel title', maxLength: 120, default: 'Event information',
    group: 'The panel on an event page', collapsed: true,
    description: 'The small line at the top of the box beside an event.',
  })
  panelHeading!: string;

  @Text({
    label: 'When it happens', maxLength: 120, default: 'Date and time',
    group: 'The panel on an event page',
  })
  panelWhenLabel!: string;

  @Text({
    label: 'Where it happens', maxLength: 120, default: 'Location',
    group: 'The panel on an event page',
  })
  panelWhereLabel!: string;

  @Text({
    label: 'Directions button', maxLength: 120, default: 'Get directions',
    group: 'The panel on an event page',
    description: 'Opens a map for the event’s location. Empty hides the button.',
  })
  panelDirectionsLabel!: string;

  @Text({
    label: 'Calendar button', maxLength: 120, default: 'Add to calendar',
    group: 'The panel on an event page',
    description: 'Empty hides the button.',
  })
  panelCalendarLabel!: string;
}
